export const T12Documentation = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Required Documents</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Complete T12 Documentation Package
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Buyers will request extensive documentation to verify your T12. Having everything organized and ready accelerates the sale process and builds buyer confidence.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl mb-6">
              <img 
                src="https://public.readdy.ai/ai/img_res/ed873e2ff6f4c656789ca0a8d0e3e13b.jpg"
                alt="Organized T12 documentation and financial records for apartment building sale"
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
              <h3 className="font-bold text-gray-900 mb-3">Documentation Best Practices</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span><strong>Digital Organization:</strong> Scan all documents and organize in clearly labeled folders</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span><strong>Consistent Naming:</strong> Use format like "2023-01-BankStatement.pdf" for easy sorting</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span><strong>Backup Everything:</strong> Keep copies in cloud storage and external drive</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span><strong>Quick Access:</strong> Be able to provide any document within 24 hours</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span><strong>Professional Presentation:</strong> Clean, legible documents create confidence</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-chart-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Core T12 Documents</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span><strong>T12 Income Statement:</strong> Monthly breakdown of all income and expenses for 12 months</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span><strong>Current Rent Roll:</strong> All units, tenant names, rent amounts, lease start/end dates</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span><strong>12 Months Bank Statements:</strong> All accounts showing deposits and withdrawals</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span><strong>3 Years Tax Returns:</strong> Complete returns with Schedule E for rental income</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Income Verification Documents</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                      <span><strong>Lease Agreements:</strong> All current tenant leases</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                      <span><strong>Rent Payment History:</strong> 12 months of rent receipts or payment records</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                      <span><strong>Other Income Records:</strong> Parking agreements, pet fee documentation, laundry income logs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                      <span><strong>Vacancy Records:</strong> Documentation of vacant units and duration</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-receipt-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Expense Verification Documents</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span><strong>Property Tax Bills:</strong> Most recent annual tax statement</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span><strong>Insurance Policies:</strong> Current policy with premium amounts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span><strong>Utility Bills:</strong> 12 months of water, sewer, electric, gas, trash</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span><strong>Maintenance Invoices:</strong> All repair and maintenance receipts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span><strong>Service Contracts:</strong> Landscaping, snow removal, pest control, elevator, etc.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span><strong>Management Agreements:</strong> Property management contract and fee structure</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Complete Due Diligence Checklist</h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-money-dollar-circle-line text-teal-600 text-xl"></i>
                Financial Documents
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">T12 Income Statement</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Current Rent Roll</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">12 Months Bank Statements</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">3 Years Tax Returns</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Current Year P&L (YTD)</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Balance Sheet</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Accounts Receivable Aging</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Security Deposit Ledger</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-file-text-line text-teal-600 text-xl"></i>
                Property Documents
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Property Deed</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Title Insurance Policy</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Survey</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Property Tax Statements</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Insurance Policies</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Environmental Reports</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Property Inspection Reports</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Zoning Documentation</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-team-line text-teal-600 text-xl"></i>
                Tenant & Operations
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">All Lease Agreements</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Tenant Applications</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Move-in/Move-out Inspections</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Service Contracts</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Vendor Contact List</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Property Management Agreement</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Maintenance Logs</span>
                </li>
                <li className="flex items-center gap-2">
                  <input type="checkbox" className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Capital Expenditure History</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 border border-green-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-speed-up-line text-2xl text-green-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Benefits of Complete Documentation</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                    <span><strong>Faster Closing:</strong> Reduces due diligence period from 60-90 days to 30-45 days</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                    <span><strong>Higher Offers:</strong> Buyers pay more for properties with clean documentation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                    <span><strong>Fewer Renegotiations:</strong> Complete records prevent last-minute price reductions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                    <span><strong>Professional Image:</strong> Shows you're a serious, organized seller</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border border-red-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Consequences of Poor Documentation</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span><strong>Deal Delays:</strong> Extended due diligence while you scramble to find documents</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span><strong>Price Reductions:</strong> Buyers assume the worst and reduce offers 10-20%</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span><strong>Lost Buyers:</strong> Serious investors walk away from disorganized sellers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span><strong>Failed Financing:</strong> Lenders refuse to fund deals with incomplete records</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};